<template>
  <div class="aveine-quiz-import d-flex align-center">
    <v-btn depressed outlined color="primary" class="mr-2" :loading="reading" @click="$refs.input.click()">
      <v-icon left>mdi-file-excel</v-icon>
      {{ $t('intranet.homeContent.quizImport.button.import') }}
    </v-btn>
    <input ref="input" type="file" class="d-none" accept=".xlsx,.xls,.ods,.csv" @change="read">

    <v-btn icon :title="$t('intranet.homeContent.quizImport.button.template')" @click="downloadTemplate()">
      <v-icon>mdi-file-download-outline</v-icon>
    </v-btn>
    <v-btn icon class="mr-2" :title="$t('intranet.homeContent.quizImport.button.rules')" @click="rulesDialog = true">
      <v-icon>mdi-help-circle-outline</v-icon>
    </v-btn>

    <!-- Les regles du fichier : ce que le modele ne dit pas a lui seul. -->
    <v-dialog v-model="rulesDialog" max-width="720" scrollable>
      <v-card>
        <v-toolbar class="pl-6 text-h6" color="primary" dark flat>
          {{ $t('intranet.homeContent.quizImport.rules.title') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="rulesDialog = false"><v-icon color="white">mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-6 text-body-2">
          <p>{{ $t('intranet.homeContent.quizImport.rules.intro') }}</p>
          <v-simple-table dense class="mb-4">
            <thead>
              <tr>
                <th>{{ $t('intranet.homeContent.quizImport.rules.column') }}</th>
                <th>{{ $t('intranet.homeContent.quizImport.rules.content') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rule in columnRules" :key="rule.column">
                <td class="text-no-wrap"><code>{{ rule.column }}</code></td>
                <td>{{ $t(`intranet.homeContent.quizImport.rules.columns.${rule.key}`, { max: maxAnswers }) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <ul>
            <li v-for="key in generalRules" :key="key">{{ $t(`intranet.homeContent.quizImport.rules.general.${key}`, { max: maxAnswers, languages: languages }) }}</li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Apercu : rien n'est ecrit tant que l'utilisateur n'a pas confirme. -->
    <v-dialog v-model="previewDialog" max-width="1100" scrollable :persistent="importing">
      <v-card>
        <v-toolbar class="pl-6 text-h6" color="primary" dark flat>
          {{ $t('intranet.homeContent.quizImport.preview.title', { file: fileName }) }}
          <v-spacer></v-spacer>
          <v-btn icon :disabled="importing" @click="close()"><v-icon color="white">mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-alert v-for="(error, index) in fileErrors" :key="`file-${index}`" type="error" dense text>
            {{ message(error) }}
          </v-alert>

          <template v-if="!fileErrors.length">
            <v-alert v-if="!done" :type="invalidCount ? 'warning' : 'info'" dense text>
              {{ $t('intranet.homeContent.quizImport.preview.summary', { valid: validCount, invalid: invalidCount }) }}
            </v-alert>
            <v-alert v-else :type="failedCount ? 'warning' : 'success'" dense text>
              {{ $t('intranet.homeContent.quizImport.preview.result', { created: createdCount, failed: failedCount }) }}
            </v-alert>

            <v-simple-table dense>
              <thead>
                <tr>
                  <th>{{ $t('intranet.homeContent.quizImport.preview.line') }}</th>
                  <th>{{ $t('intranet.homeContent.quiz.day') }}</th>
                  <th>{{ $t('intranet.homeContent.quiz.question') }}</th>
                  <th>{{ $t('intranet.homeContent.quiz.answers') }}</th>
                  <th>{{ $t('intranet.homeContent.attributes.published') }}</th>
                  <th>{{ $t('intranet.homeContent.quizImport.preview.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.line">
                  <td>{{ row.line }}</td>
                  <td class="text-no-wrap">{{ row.day || '—' }}</td>
                  <td>{{ row.question[$i18n.locale] || row.question.fr || row.question.en || '—' }}</td>
                  <td>{{ row.answers.length }}</td>
                  <td>{{ row.published ? $t('intranet.homeContent.quizImport.yes') : $t('intranet.homeContent.quizImport.no') }}</td>
                  <td class="py-1">
                    <v-chip v-if="row.status === 'created'" small color="success">{{ $t('intranet.homeContent.quizImport.status.created') }}</v-chip>
                    <v-chip v-else-if="row.status === 'importing'" small color="info">{{ $t('intranet.homeContent.quizImport.status.importing') }}</v-chip>
                    <v-chip v-else-if="!row.errors.length && !row.status" small color="grey lighten-2">{{ $t('intranet.homeContent.quizImport.status.ready') }}</v-chip>
                    <div v-for="(error, index) in row.errors" :key="index" class="error--text text-caption">
                      {{ message(error) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="importing" @click="close()">
            {{ done ? $t('intranet.homeContent.quizImport.button.close') : $t('intranet.referential.button.cancel') }}
          </v-btn>
          <v-btn
            v-if="!done"
            depressed
            color="primary"
            :disabled="!validCount || !!fileErrors.length"
            :loading="importing"
            @click="importRows()"
          >
            {{ $t('intranet.homeContent.quizImport.button.confirm', { count: validCount }) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'
import {
  LANGUAGES, MAX_ANSWERS, checkExistingDays, createQuiz, downloadTemplate, parseWorkbook
} from '@/store/quiz-import'

/**
 * Import de quizz depuis un fichier Excel : bouton d'import, modele a
 * telecharger, regles du fichier, puis apercu ligne a ligne avant d'ecrire.
 *
 * Seules les lignes sans erreur sont importees ; une ligne en erreur l'est
 * pour une raison affichee, jamais en silence. Emet `imported` quand au moins
 * un quizz a ete cree, pour que la liste se recharge.
 */
export default {
  name: 'AveineQuizImport',
  props: {
    APIbaseURL: String
  },
  data: () => ({
    rulesDialog: false,
    previewDialog: false,
    reading: false,
    importing: false,
    done: false,
    fileName: '',
    fileErrors: [],
    rows: [],
    maxAnswers: MAX_ANSWERS,
    languages: LANGUAGES.join(', '),
    columnRules: [
      { column: 'jour', key: 'day' },
      { column: 'theme_fr, theme_en', key: 'theme' },
      { column: 'question_fr, question_en', key: 'question' },
      { column: 'explication_fr, explication_en', key: 'explanation' },
      { column: `reponse1_fr … reponse${MAX_ANSWERS}_en`, key: 'answers' },
      { column: 'bonne_reponse', key: 'correct' },
      { column: 'publie', key: 'published' }
    ],
    generalRules: ['oneRow', 'headers', 'languages', 'dayTaken', 'duplicates', 'partial', 'example']
  }),
  computed: {
    validCount () {
      return this.rows.filter(row => !row.errors.length && !row.status).length
    },
    invalidCount () {
      return this.rows.filter(row => row.errors.length).length
    },
    createdCount () {
      return this.rows.filter(row => row.status === 'created').length
    },
    failedCount () {
      return this.rows.filter(row => row.status === 'failed').length
    }
  },
  methods: {
    downloadTemplate,

    message (error) {
      const params = { ...(error.params || {}) }
      if (params.field) params.field = this.$t(`intranet.homeContent.quiz.${params.field}`)
      return this.$t(`intranet.homeContent.quizImport.errors.${error.code}`, params)
    },

    async read (event) {
      const file = event.target.files[0]
      // Vide le champ : reprendre le meme fichier corrige doit relancer `change`.
      event.target.value = ''
      if (!file) return

      this.reading = true
      this.done = false
      this.fileName = file.name
      let parsed
      try {
        parsed = parseWorkbook(await file.arrayBuffer())
      } catch (exception) {
        this.reading = false
        this.$toastr.e(this.$t('intranet.homeContent.quizImport.errors.unreadable'))
        return
      }

      try {
        if (!parsed.errors.length) {
          await checkExistingDays(parsed.rows, { baseURL: this.APIbaseURL, locale: this.$i18n.locale })
        }
        this.fileErrors = parsed.errors
        this.rows = parsed.rows
        this.previewDialog = true
      } catch (errors) {
        // Sans ce controle, l'apercu annoncerait « pretes » des lignes que
        // CRUD-Wine refusera : on n'ouvre rien.
        errorHandler(errors, this)
      } finally {
        this.reading = false
      }
    },

    /**
     * Une ligne apres l'autre : deux creations en parallele se disputeraient
     * l'index unique des bonnes reponses si elles partageaient un quizz, et
     * l'ordre du fichier reste celui du resultat.
     */
    async importRows () {
      this.importing = true
      for (const row of this.rows) {
        if (row.errors.length || row.status) continue
        this.$set(row, 'status', 'importing')
        try {
          await createQuiz(row, { baseURL: this.APIbaseURL, locale: this.$i18n.locale })
          row.status = 'created'
        } catch (exception) {
          row.status = 'failed'
          row.errors.push(this.isDayTaken(exception)
            ? { code: 'dayTakenRace' }
            : { code: 'saveFailed', params: { detail: this.detail(exception) } })
        }
      }
      this.importing = false
      this.done = true
      if (this.createdCount) this.$emit('imported')
    },

    /** Le refus de CRUD-Wine quand le jour est pris, comme dans le formulaire. */
    isDayTaken (exception) {
      const body = exception && exception.response && exception.response.data
      if (!body || !Array.isArray(body.errors)) return false
      return body.errors.some(e => /day.*unique|already scheduled|quizzes_day_unique/.test(e.detail || ''))
    },

    detail (exception) {
      const body = exception && exception.response && exception.response.data
      const first = body && Array.isArray(body.errors) && body.errors[0]
      return (first && (first.detail || first.title)) || exception.message || '—'
    },

    close () {
      this.previewDialog = false
      this.rows = []
      this.fileErrors = []
    }
  }
}
</script>
